import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Teleport as _Teleport, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("strong", { class: "text-xl leading-8 block font-normal text-gray-500 mt-[1.3333333em]" }, "The domain name system (DNS) plays an important role in delivering email.", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The owner of a domain name (for example: "),
  /*#__PURE__*/_createElementVNode("code", null, "@gmail.com"),
  /*#__PURE__*/_createTextVNode(") can specify where mail should be delivered. It can also prove that it has sent an email on behalf of the domain name owner. Other mail servers need some way to look up the email configuration of any domain name. That is why this configuration is stored in DNS records.")
], -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("strong", { class: "font-medium" }, "There are five types of email configurations that the DNS can store:", -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("figure", { class: "mt-6" }, [
  /*#__PURE__*/_createElementVNode("img", {
    class: "w-full rounded-lg",
    src: "/img/learning/mx-vs-spf-vs-dmarc-vs-dkim-vs-bimi/spam-filtering.jpg",
    alt: "Spam filters are like an immume system of email, consisting of many different components.",
    title: "Spam filters are like an immume system of email, consisting of many different components.",
    width: "1200",
    height: "600"
  }),
  /*#__PURE__*/_createElementVNode("figcaption", null, [
    /*#__PURE__*/_createTextVNode("Spam filters are like an immume system of email, consisting of many different components. By NsLookup.io. Licensed under "),
    /*#__PURE__*/_createElementVNode("a", {
      href: "https://creativecommons.org/licenses/by/4.0/",
      title: "Creative Commons By 4.0 licence",
      rel: "nofollow"
    }, "CC By 4.0"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("h2", null, "MX: Specifying mail servers", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("p", null, "In addition to specifying the mail server, the MX record also includes a preference value. This value determines the order in which mail servers will be tried when multiple MX records exist for a domain. The mail server with the lowest preference value will be tried first, followed by the mail server with the next lowest value, and so on. This allows the owner of a domain to specify a primary mail server and one or more backup mail servers in case the primary server is unavailable.", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("h2", null, "SPF: Allowing servers to send email", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("p", null, "When a mail server receives an email from a domain, it will perform a DNS TXT lookup to find the SPF record for the domain. If the server that sent the email is not listed in the SPF record, the email may be marked as spam, or rejected. This helps to protect both the recipient of from spam and phishing. And it protects the domain reputation of the sender's domain.", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("h2", null, "DKIM: Proving email integrity", -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("When an email is sent, the sending mail server will generate a digital signature for the email using a private key. It includes this signature in the email as a "),
  /*#__PURE__*/_createElementVNode("code", null, "DKIM-Signature"),
  /*#__PURE__*/_createTextVNode(" header.")
], -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("p", null, "The receiving mail server will perform a DNS TXT lookup to find the DKIM record for the domain. The record will include the public key that corresponds to the private key that was used to generate the signature. The receiving mail server can then use this public key to verify the integrity of the email.", -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", null, "If the signature is valid, it indicates that the email has not been tampered with and was sent by an authorized server. This helps to protect the sender and recipient of the email, as it ensures that no unauthorized third party can change the email. It also helps to prevent spam and phishing, as the receiving mail server can verify that the email was actually sent by the domain that it claims to be from.", -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("h2", null, "DMARC: Providing policies for non-conforming emails", -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("p", null, "When a mail server receives an email from a domain, it will perform a DNS TXT lookup to find the DMARC record for the domain. If the email does not conform to the SPF or DKIM standards, the receiving mail server will use the instructions in the DMARC record to determine how to handle the email. The DMARC record may specify that such emails should be rejected, or that they should be marked as spam.", -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("p", null, "In addition to specifying the policies for handling unauthenticated email, the DMARC record may also include a reporting mechanism. This allows the owner of the domain to receive reports from other mail servers about how their domain's email is being handled. This can help the domain owner to identify potential issues with their email configuration.", -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("h2", null, "BIMI: Specifying a logo", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("p", null, "The BIMI (Brand Indicators for Message Identification) record allows the owner of a domain to specify a logo that email clients should show. This can help to increase the visibility and recognition of the domain's brand. It also improves the user experience for recipients of email from the domain.", -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("p", null, "To use BIMI, the domain owner must first create a digital version of their logo, and then publish a BIMI record in the DNS. The record should include the URL of the logo. When an email is received from the domain, the email client will perform a DNS lookup to find the BIMI record. If the record is found, the email client may display the specified logo next to the sender's name in the email.", -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("p", null, "BIMI is an optional feature, and not all email clients support it. Most of those who do will only display the logo, if there is a VMC certificate for the domain name. Getting such a certificate requires a brand trademark and involves a verification process. Not to mention the $1000 yearly price tag. This is why most domain names don’t have BIMI configured.", -1)

import BlogPost from '@/components/layout/BlogPost.vue';

import { ruurtjan } from '@/common/authors';
import { learningBreadcrumb } from '@/model/Breadcrumb';
import { BlogMeta } from '@/common/blogMeta';

const meta: BlogMeta = {
  tag: 'Email and the DNS',
  url: '/learning/mx-vs-spf-vs-dmarc-vs-dkim-vs-bimi/',
  breadcrumbs: [learningBreadcrumb],
  title: 'MX vs SPF vs DMARC vs DKIM vs BIMI',
  description: 'Learn about the five DNS records that help control email delivery and spam prevention: MX, SPF, DKIM, DMARC, and BIMI.',
  overviewImage: '/img/learning/mx-vs-spf-vs-dmarc-vs-dkim-vs-bimi/spam-filtering-small.jpg',
  overviewImageAlt: 'MX vs SPF vs DMARC vs DKIM vs BIMI',
  overviewDescription: 'Learn about the five DNS records that help control email delivery and spam prevention: MX, SPF, DKIM, DMARC, and BIMI.',
  author: ruurtjan,
  editor: undefined,
  img1024x512: 'https://www.nslookup.io/img/learning/mx-vs-spf-vs-dmarc-vs-dkim-vs-bimi/mx-vs-spf-vs-dmarc-vs-dkim-vs-bimi-social.jpg',
  img1200x630: 'https://www.nslookup.io/img/learning/mx-vs-spf-vs-dmarc-vs-dkim-vs-bimi/mx-vs-spf-vs-dmarc-vs-dkim-vs-bimi-social.jpg',
  written: new Date('2022-12-08T11:22:08.115Z'),
  updated: new Date('2023-03-28T12:00:16.338Z'),
};
const __default__ = {
  meta,
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'MxVsSpfVsDmarcVsDkimVsBimi',
  setup(__props) {


return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"ImageObject\", \"author\": { \"@type\": \"Person\", \"email\": \"mailto:ruurtjan@nslookup.io\", \"image\": \"https://www.nslookup.io/img/ruurtjan-lg.jpg\", \"jobTitle\": \"CEO\", \"name\": \"Ruurtjan Pul\", \"url\": \"https://www.nslookup.io/about/ruurtjan-pul/\" }, \"copyrightHolder\": { \"@type\": \"Organization\", \"url\": \"https://www.nslookup.io/\", \"email\": \"hello@nslookup.io\" }, \"copyrightYear\": 2023, \"contentUrl\": \"https://www.nslookup.io/img/learning/mx-vs-spf-vs-dmarc-vs-dkim-vs-bimi/spam-filtering.jpg\", \"license\": \"https://creativecommons.org/licenses/by/4.0/\", \"acquireLicensePage\": \"https://creativecommons.org/licenses/by/4.0/\" } ")
        ]),
        _: 1
      }))
    ])),
    _createVNode(BlogPost, {
      title: meta.title,
      description: meta.description,
      url: `https://www.nslookup.io${meta.url}`,
      breadcrumbs: meta.breadcrumbs,
      img1024x512: meta.img1024x512,
      img1200x630: meta.img1200x630,
      written: meta.written,
      updated: meta.updated,
      author: meta.author,
      editor: meta.editor
    }, {
      default: _withCtx(() => [
        _hoisted_1,
        _hoisted_2,
        _hoisted_3,
        _createElementVNode("ul", null, [
          _createElementVNode("li", null, [
            _createTextVNode("Which mail server email should be sent to ("),
            _createVNode(_component_router_link, { to: "/learning/dns-record-types/mx/" }, {
              default: _withCtx(() => [
                _createTextVNode("MX")
              ]),
              _: 1
            }),
            _createTextVNode(")")
          ]),
          _createElementVNode("li", null, [
            _createTextVNode("Which servers are allowed to send email from a domain ("),
            _createVNode(_component_router_link, { to: "/learning/spf-a-practical-guide/" }, {
              default: _withCtx(() => [
                _createTextVNode("SPF")
              ]),
              _: 1
            }),
            _createTextVNode(")")
          ]),
          _createElementVNode("li", null, [
            _createTextVNode("Which public key can be used to verify the integrity of an email ("),
            _createVNode(_component_router_link, { to: "/learning/dkim-a-practical-guide/" }, {
              default: _withCtx(() => [
                _createTextVNode("DKIM")
              ]),
              _: 1
            }),
            _createTextVNode(")")
          ]),
          _createElementVNode("li", null, [
            _createTextVNode("What should happen when an email does not conform to SPF or DKIM ("),
            _createVNode(_component_router_link, { to: "/learning/dmarc-a-practical-guide/" }, {
              default: _withCtx(() => [
                _createTextVNode("DMARC")
              ]),
              _: 1
            }),
            _createTextVNode(")")
          ]),
          _createElementVNode("li", null, [
            _createTextVNode("Which logo should be displayed in email clients ("),
            _createVNode(_component_router_link, { to: "#bimi-specifying-a-logo" }, {
              default: _withCtx(() => [
                _createTextVNode("BIMI")
              ]),
              _: 1
            }),
            _createTextVNode(")")
          ])
        ]),
        _hoisted_4,
        _hoisted_5,
        _createElementVNode("p", null, [
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/mx/" }, {
            default: _withCtx(() => [
              _createTextVNode("The MX (Mail Exchanger) record")
            ]),
            _: 1
          }),
          _createTextVNode(" specifies which mail server handles incoming mail for a given domain. When a mail server sends an email for a domain, it will perform a DNS lookup to find the MX record for that domain. It then delivers the email to the specified mail server. This allows the owner of a domain to control where their email is delivered.")
        ]),
        _hoisted_6,
        _hoisted_7,
        _createElementVNode("p", null, [
          _createVNode(_component_router_link, { to: "/learning/spf-a-practical-guide/" }, {
            default: _withCtx(() => [
              _createTextVNode("The SPF (Sender Policy Framework) record")
            ]),
            _: 1
          }),
          _createTextVNode(" specifies which servers are allowed to send email on behalf of a given domain. This is an important security measure. It helps to prevent spam and phishing by unauthorized mail servers.")
        ]),
        _hoisted_8,
        _hoisted_9,
        _createElementVNode("p", null, [
          _createVNode(_component_router_link, { to: "/learning/dkim-a-practical-guide/" }, {
            default: _withCtx(() => [
              _createTextVNode("The DKIM (DomainKeys Identified Mail) record")
            ]),
            _: 1
          }),
          _createTextVNode(" allows a domain owner to prove the integrity of an email. This is done by attaching a digital signature to the email. Receiving mail servers can verify this signature using the DKIM record.")
        ]),
        _hoisted_10,
        _hoisted_11,
        _hoisted_12,
        _hoisted_13,
        _createElementVNode("p", null, [
          _createVNode(_component_router_link, { to: "/learning/dmarc-a-practical-guide/" }, {
            default: _withCtx(() => [
              _createTextVNode("The DMARC (Domain-based Message Authentication, Reporting and Conformance) record")
            ]),
            _: 1
          }),
          _createTextVNode(" specifies the policies that should be applied to emails that do not conform to the SPF or DKIM standards. This allows the owner of a domain to control how other mail servers handle email when it doesn't pass these checks.")
        ]),
        _hoisted_14,
        _hoisted_15,
        _hoisted_16,
        _hoisted_17,
        _hoisted_18,
        _hoisted_19
      ]),
      _: 1
    }, 8, ["title", "description", "url", "breadcrumbs", "img1024x512", "img1200x630", "written", "updated", "author", "editor"])
  ], 64))
}
}

})